import React, { useEffect } from 'react';
import {
  Badge,
  Drawer,
  Avatar,
  ListItemButton,
  Container,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { M3BodyLarge } from './reusable/TextStyles';
import { labelForRole } from '../utils/roleLabel';
import { PROVIDER_READ_ROLES, PROVIDER_WRITE_ROLES } from '../utils/constants';
import styled from '@emotion/styled';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import BoltIcon from '@mui/icons-material/Bolt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import WorkIcon from '@mui/icons-material/Work';
import GroupIcon from '@mui/icons-material/Group';
import InsightsIcon from '@mui/icons-material/Insights';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ChatIcon from '@mui/icons-material/Chat';
import CampaignIcon from '@mui/icons-material/Campaign';
import EmailIcon from '@mui/icons-material/Email';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PolicyIcon from '@mui/icons-material/Policy';
import RuleFolderIcon from '@mui/icons-material/RuleFolder';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Logo from '../assets/Logo.svg?react';
import { useLocation } from 'react-router-dom';
import theme from '../utils/theme';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import quickcareLogo from '../assets/splash.png';
import Loading from './reusable/Loading';
import { useResponsive } from '../hooks/useResponsive';
import { useEmbeddedMode } from '../hooks/useEmbeddedMode';
import { DrawerContext } from '../utils/context';
import { usePendingOrganisationsCount } from './pages/Organisation/hooks/useOrganisations';

const drawerWidth = '290px';
const drawerWidthClosed = '70px';
const transitionDuration = '0.3s';
const transitionEasing = 'cubic-bezier(0.4, 0, 0.2, 1)';

/**
 * Wraps the drawer's avatar / name in a link to the self-profile page.
 * When `enabled` is false it renders the children untouched, so logins with no
 * /profile access (admins, educators) keep a plain, non-interactive footer
 * instead of a link that would just bounce them back to '/'.
 *
 * `display: contents` keeps the anchor out of the flex layout entirely — the
 * avatar and the name block are flex siblings of the logout button, so an
 * ordinary wrapper element would collapse them into one flex item.
 */
const ProfileLink = ({ enabled, onNavigate, children }) =>
  enabled ? (
    <Link
      to='/profile'
      onClick={onNavigate}
      style={{ display: 'contents', textDecoration: 'none', color: 'inherit' }}
    >
      {children}
    </Link>
  ) : (
    children
  );

const openedMixin = (theme) => ({
  width: drawerWidth,
  overflowX: 'visible',
  transition: `width ${transitionDuration} ${transitionEasing}`,
});

const closedMixin = (theme) => ({
  overflowX: 'visible',
  width: drawerWidthClosed,
  transition: `width ${transitionDuration} ${transitionEasing}`,
});

const Layout = ({
  children,
  isLoggedIn,
  isAdmin,
  onLogout,
  loading,
  currentUser,
  currentRole,
  logOutLoading,
}) => {
  const { isMobile, isTablet } = useResponsive();
  const { isMobileApp } = useEmbeddedMode();
  const [open, setOpen] = React.useState(true);
  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  const location = useLocation();

  // QC-2927: self-signed-up organisations waiting on "Mark terms signed" —
  // shown as a count on the Organisation entry so Ops notice a signup without
  // opening the list. Admin-only; nobody else can reach the endpoint.
  const { data: pendingOrganisations = 0 } = usePendingOrganisationsCount({ enabled: !!isAdmin });

  // The drawer's name + avatar link through to the self-profile page. Only the
  // roles that can actually reach /profile get the affordance — the route
  // redirects anyone else to '/', so linking an admin (or any other login)
  // there would hand them a dead end. PROVIDER_READ_ROLES
  // (owner/manager/centre_director/support) is the same set that gates the
  // ACCOUNT nav item and, via hasProviderProfileAccess, the /profile route
  // itself — keep all three in step so the affordance never outlives its
  // destination. co_only is deliberately absent: it has its own profile
  // surface, and the drawer is hidden for it entirely (see isEducator below).
  const canOpenOwnProfile =
    !isAdmin && PROVIDER_READ_ROLES.includes(currentRole);

  const getLinks = () => {
    if (isAdmin) {
      return [
        {
          section: 'AI',
          items: [
            { route: '/ai-dashboard', name: 'AI Dashboard', icon: <BoltIcon /> },
          ],
        },
        {
          section: 'STAFFING',
          items: [
            { route: '/shifts', name: 'Shifts', icon: <CalendarMonthIcon /> },
            { route: '/timesheets', name: 'Timesheets', icon: <PendingActionsIcon /> },
            { route: '/shift-groups', name: 'Jobs', icon: <WorkIcon /> },
          ],
        },
        {
          section: 'WORKFORCE',
          items: [
            { route: '/admin/compliance', name: 'Compliance', icon: <InsightsIcon /> },
            { route: '/educators', name: 'Educators', icon: <GroupIcon /> },
            { route: '/national-register', name: 'National Register', icon: <FactCheckIcon /> },
            { route: '/reports', name: 'Reports', icon: <SummarizeIcon /> },
          ],
        },
        {
          section: 'COMMS',
          items: [
            { route: '/messages', name: 'Support Messages', icon: <ChatIcon /> },
            { route: '/support-email', name: 'Support Email', icon: <EmailIcon /> },
            { route: '/admin-send-message', name: 'Announcements', icon: <CampaignIcon /> },
          ],
        },
        {
          section: 'SETTINGS',
          items: [
            { route: '/organisation', name: 'Organisation', icon: <ApartmentIcon />, badge: pendingOrganisations },
            { route: '/logins-manage', name: 'User Accounts', icon: <ManageAccountsIcon /> },
            { route: '/health', name: 'System Health', icon: <MonitorHeartIcon /> },
            // QC-2927: refresh the local copy of the ACECQA national register
            // that the provider signup flow searches.
            { route: '/admin/acecqa-register', name: 'ACECQA Register', icon: <LibraryBooksIcon /> },
            // Editing the Quickcare (org 0) document rules is a super-user
            // capability — only show the entry to flagged admins.
            ...(currentUser?.is_super_user
              ? [{ route: '/admin/document-rules', name: 'Quickcare Document Rules', icon: <RuleFolderIcon /> }]
              : []),
          ],
        },
      ];
    }

    if (PROVIDER_READ_ROLES.includes(currentRole)) {
      // QC-2932: an owner can switch the Educators / Compliance surfaces off for
      // an individual login. Both capabilities default ON, so only an explicit
      // false hides the nav entry. `.some()` over the memberships mirrors
      // canApproveSomewhere below — a multi-org login keeps the entry while any
      // of their orgs still allows it, and the API enforces it per organisation
      // (requireOrgCapability in api/middleware/orgCapabilityAccess.ts). Owners
      // hold both implicitly; the server already projects an effective-true for
      // them, so no role special-casing is needed here.
      const orgs = currentUser?.organisations || [];
      const canExploreEducators = orgs.some((o) => o.can_explore_educators !== false);
      const canViewCompliance = orgs.some((o) => o.can_view_compliance !== false);
      const sections = [
        {
          section: 'STAFFING',
          items: [
            { route: '/shifts', name: 'Shifts', icon: <CalendarMonthIcon /> },
            { route: '/shift-groups', name: 'Jobs', icon: <WorkIcon /> },
          ],
        },
        {
          section: 'WORKFORCE',
          items: [
            ...(canViewCompliance
              ? [{ route: '/compliance', name: 'Compliance', icon: <InsightsIcon /> }]
              : []),
            ...(canExploreEducators
              ? [{ route: '/educators', name: 'Educators', icon: <GroupIcon /> }]
              : []),
            // QC-2847: reports are owner-only — an export carries the whole
            // workforce's contact details and compliance state out of the
            // product. The API enforces this; the nav just avoids showing a
            // link that would 403.
            ...(currentRole === 'owner'
              ? [{ route: '/reports', name: 'Reports', icon: <SummarizeIcon /> }]
              : []),
          ],
        },
      ];

      if (PROVIDER_WRITE_ROLES.includes(currentRole)) {
        sections[1].items.push(
          { route: '/national-register', name: 'National Register', icon: <FactCheckIcon /> },
        );
        // Show Pending Approvals only if this user can approve in at least one
        // flagged org. Centre directors (access==='centre_director') never see
        // this entry — they're submitters, not approvers, so canApproveSomewhere
        // is always false for them.
        const canApproveSomewhere = (currentUser?.organisations || []).some(
          (o) => ['owner', 'manager'].includes(o.access) && o.enable_shift_approval_workflow
        );
        if (canApproveSomewhere) {
          sections[0].items.push(
            { route: '/pending-approvals', name: 'Pending Approvals', icon: <PendingActionsIcon /> },
          );
        }
        sections.push({
          section: 'SETTINGS',
          items: [
            { route: '/organisation', name: 'Organisation', icon: <ApartmentIcon /> },
            // User Accounts lists the org's people too, so it rides on the same
            // capability as Educators above — GET /organisations/:id/staff 403s
            // without it, and this just avoids showing a link that would fail.
            ...(canExploreEducators
              ? [{ route: '/logins-manage', name: 'User Accounts', icon: <ManageAccountsIcon /> }]
              : []),
            { route: '/policies', name: 'Policies', icon: <PolicyIcon /> },
          ],
        });
      }

      // Every provider-side role gets their own profile — a support login has
      // documents to keep current just like a manager does. Pushed outside the
      // write-roles block so it lands last, after SETTINGS, for the roles that
      // have one.
      sections.push({
        section: 'ACCOUNT',
        items: [
          { route: '/profile', name: 'Your Profile', icon: <AccountCircleIcon /> },
        ],
      });
      return sections;
    }

    return [];
  };
  const isEducator = (currentRole === 'educator' || currentRole === null || currentRole === 'co_only') && !isAdmin;
  const isHidden =
    (location.pathname.includes('/signup') ||
    location.pathname.includes('/reset/') ||
    location.pathname.includes('/signing') ||
    isEducator||
    location.pathname.includes('/stripe')) || (!isLoggedIn) || isMobileApp;
  useEffect(() => {
    if (!isLoggedIn && !loading) {
      setOpen(true);
    }
  }, [isLoggedIn, loading]);

  useEffect(() => {
    if (location.pathname.includes('/timesheets')) {
      setOpen(false);
    }
  }, [location.pathname]);

  const prevBreakpointRef = React.useRef(null);
  useEffect(() => {
    const currentBreakpoint = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';
    if (prevBreakpointRef.current === currentBreakpoint) return;
    prevBreakpointRef.current = currentBreakpoint;

    if (isMobile) {
      setOpen(false);
    } else if (isTablet) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isMobile, isTablet]);

  const renderNavItem = (link, isDrawerOpen, onItemClick) => {
    const isActive = location.pathname === link.route;
    return (
      <Tooltip
        key={link.route}
        title={link.name}
        placement='right'
        disableHoverListener={isDrawerOpen}
      >
        <NavItem
          component={Link}
          to={link.route}
          selected={isActive}
          onClick={onItemClick}
          sx={{
            background: isActive ? '#F3F0FF' : 'transparent',
            color: isActive ? theme.palette.primary.main : '#374151',
            justifyContent: isDrawerOpen ? 'flex-start' : 'center',
            '&:hover': {
              background: isActive ? '#F3F0FF' : '#F9FAFB',
            },
            '&.Mui-selected': {
              background: '#F3F0FF',
              '&:hover': {
                background: '#F3F0FF',
              },
            },
          }}
        >
          {isActive && (
            <ActiveIndicator />
          )}
          <Badge
            badgeContent={isDrawerOpen ? 0 : link.badge || 0}
            color='error'
            overlap='circular'
            max={99}
            sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16, padding: '0 4px' } }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 22,
                height: 22,
                flexShrink: 0,
                fontSize: 22,
                color: isActive ? theme.palette.primary.main : '#374151',
              }}
            >
              {link.icon}
            </Box>
          </Badge>
          {isDrawerOpen && (
            <M3BodyLarge
              style={{
                paddingLeft: '12px',
                fontWeight: isActive ? 600 : 500,
                fontSize: 14,
                whiteSpace: 'nowrap',
                color: 'inherit',
              }}
            >
              {link.name}
            </M3BodyLarge>
          )}
          {isDrawerOpen && link.badge > 0 && (
            <NavCount aria-label={`${link.badge} pending`}>{link.badge > 99 ? '99+' : link.badge}</NavCount>
          )}
        </NavItem>
      </Tooltip>
    );
  };

  const renderNavItems = (sections, isDrawerOpen, onItemClick) => (
    <NavItemsContainer>
      {sections.map((group, index) => (
        <Box key={group.section}>
          {index > 0 && (
            <Box sx={{ height: '1px', background: '#E5E7EB', margin: isDrawerOpen ? '8px 12px' : '4px 8px' }} />
          )}
          {isDrawerOpen && (
            <SectionHeader>{group.section}</SectionHeader>
          )}
          {group.items.map((link) => renderNavItem(link, isDrawerOpen, onItemClick))}
        </Box>
      ))}
    </NavItemsContainer>
  );

  return (
    <div style={{ display: 'flex', backgroundColor: '#FBFBFB', minHeight: '100vh' }}>
      {isHidden ? null : (
        <nav aria-label="Main navigation">
          {isMobile ? (
            <Drawer
              variant='temporary'
              open={open}
              onClose={() => setOpen(false)}
              PaperProps={{
                style: {
                  width: drawerWidth,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  border: 'none',
                },
              }}
              ModalProps={{
                keepMounted: true,
              }}
            >
              {isLoggedIn && (
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #5B08C0 100%)`,
                    padding: '20px',
                  }}
                >
                  {/* Close button row */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                    <IconButton
                      onClick={() => setOpen(false)}
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '8px',
                        background: 'rgba(255,255,255,0.15)',
                        color: '#FFFFFF',
                        '&:hover': {
                          background: 'rgba(255,255,255,0.25)',
                        },
                      }}
                      aria-label='Close menu'
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>

                  {/* User info + logout row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Tapping the avatar or name opens /profile and closes the
                        temporary drawer behind it — otherwise the overlay would
                        stay up over the page it just navigated to. */}
                    <ProfileLink enabled={canOpenOwnProfile} onNavigate={() => setOpen(false)}>
                    <CustomAvatar
                      alt={`${currentUser?.first_name} ${currentUser?.last_name}`}
                      sx={{
                        width: 42,
                        height: 42,
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        border: '2px solid rgba(255,255,255,0.4)',
                        fontSize: 14,
                        fontWeight: 700,
                        flexShrink: 0,
                        cursor: canOpenOwnProfile ? 'pointer' : 'default',
                      }}
                      src={currentUser?.avatar_url}
                      {...stringAvatar(
                        `${currentUser?.first_name} ${currentUser?.last_name}`,
                      )}
                    />
                    <Box sx={{ flex: 1, minWidth: 0, cursor: canOpenOwnProfile ? 'pointer' : 'default' }}>
                      <Box
                        sx={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: '#FFFFFF',
                          lineHeight: '20px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {currentUser?.first_name} {currentUser?.last_name}
                      </Box>
                      <Box
                        sx={{
                          fontSize: 12,
                          color: 'rgba(255,255,255,0.7)',
                          lineHeight: '16px',
                          marginTop: '1px',
                          textTransform: 'capitalize',
                        }}
                      >
                        {currentRole ? labelForRole(currentRole) : 'User'}
                      </Box>
                    </Box>
                    </ProfileLink>
                    <IconButton
                      onClick={onLogout}
                      disabled={logOutLoading}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.3)',
                        background: 'rgba(255,255,255,0.15)',
                        color: '#FFFFFF',
                        fontSize: 13,
                        fontWeight: 600,
                        flexShrink: 0,
                        backdropFilter: 'blur(4px)',
                        '&:hover': {
                          background: 'rgba(255,255,255,0.25)',
                        },
                      }}
                      aria-label='Logout'
                    >
                      <LogoutIcon sx={{ fontSize: 18 }} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>Log out</span>
                    </IconButton>
                  </Box>
                </Box>
              )}
              {renderNavItems(getLinks(), true, () => setOpen(false))}
            </Drawer>
          ) : (
          <CustomDrawer
            PaperProps={{
              sx: {
                width: open ? drawerWidth : drawerWidthClosed,
                transition: `width ${transitionDuration} ${transitionEasing}`,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflowX: 'visible !important',
                overflowY: 'auto',
              },
            }}
            variant='permanent'
            open={open}
            style={{
              width: open ? drawerWidth : drawerWidthClosed,
              transition: `width ${transitionDuration} ${transitionEasing}`,
            }}
          >
            {/* Logo header */}
            <LogoHeader style={{ padding: open ? '40px 20px' : '20px 16px', justifyContent: open ? 'flex-start' : 'center' }}>
              {open ? (
                <Link to='/' style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                  <Logo style={{ width: '160px', height: 'auto' }} role="img" aria-label="QuickCare logo" />
                </Link>
              ) : (
                <Link to='/'>
                  <img
                    src={quickcareLogo}
                    alt='QuickCare logo'
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                    }}
                  />
                </Link>
              )}
            </LogoHeader>

            {renderNavItems(getLinks(), open)}

            {/* Profile footer */}
            {isLoggedIn && (
              <ProfileSection>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px',
                    borderRadius: '10px',
                    justifyContent: open ? 'flex-start' : 'center',
                  }}
                >
                  <ProfileLink enabled={canOpenOwnProfile}>
                    <Tooltip
                      title={canOpenOwnProfile
                        ? 'View your profile'
                        : `${currentUser?.first_name} ${currentUser?.last_name}`}
                      placement='right'
                      disableHoverListener={open && !canOpenOwnProfile}
                    >
                      <CustomAvatar
                        alt={`${currentUser?.first_name} ${currentUser?.last_name}`}
                        sx={{
                          width: 36,
                          height: 36,
                          background: `linear-gradient(135deg, #06B6D4, ${theme.palette.primary.main})`,
                          fontSize: 13,
                          fontWeight: 700,
                          flexShrink: 0,
                          cursor: canOpenOwnProfile ? 'pointer' : 'default',
                        }}
                        src={currentUser?.avatar_url}
                        {...stringAvatar(
                          `${currentUser?.first_name} ${currentUser?.last_name}`,
                        )}
                      />
                    </Tooltip>
                    {open && (
                      <Box sx={{ flex: 1, minWidth: 0, cursor: canOpenOwnProfile ? 'pointer' : 'default' }}>
                        <Box
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#111827',
                            lineHeight: '18px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            ...(canOpenOwnProfile && { '&:hover': { color: theme.palette.primary.main } }),
                          }}
                        >
                          {currentUser?.first_name} {currentUser?.last_name}
                        </Box>
                        <Box sx={{ fontSize: 12, color: '#9CA3AF', lineHeight: '16px', textTransform: 'capitalize' }}>
                          {currentRole ? labelForRole(currentRole) : 'User'}
                        </Box>
                      </Box>
                    )}
                  </ProfileLink>
                  {open && (
                      <Tooltip title='Log out' placement='right'>
                        <IconButton
                          onClick={onLogout}
                          disabled={logOutLoading}
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '8px',
                            border: '1px solid #FEE2E2',
                            background: '#FEF2F2',
                            color: '#DC2626',
                            flexShrink: 0,
                            '&:hover': {
                              background: '#FEE2E2',
                              borderColor: '#FECACA',
                            },
                          }}
                          aria-label='Logout'
                        >
                          <LogoutIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                  )}
                </Box>
                {!open && (
                  <Tooltip title='Log out' placement='right'>
                    <IconButton
                      onClick={onLogout}
                      disabled={logOutLoading}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        padding: '10px 0',
                        borderRadius: '8px',
                        marginTop: '4px',
                        border: '1px solid #FEE2E2',
                        background: '#FEF2F2',
                        color: '#DC2626',
                        '&:hover': {
                          background: '#FEE2E2',
                        },
                      }}
                      aria-label='Logout'
                    >
                      <LogoutIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </ProfileSection>
            )}
          </CustomDrawer>
          )}
          {/* Collapse toggle - outside drawer so it overlaps page content */}
          {!isMobile && (
            <CollapseToggle
              onClick={handleDrawerOpen}
              aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
              style={{
                left: open ? `calc(${drawerWidth} - 14px)` : `calc(${drawerWidthClosed} - 14px)`,
              }}
            >
              {open ? (
                <ChevronLeft sx={{ fontSize: 18 }} />
              ) : (
                <ChevronRight sx={{ fontSize: 18 }} />
              )}
            </CollapseToggle>
          )}
        </nav>
      )}
      <Container
        component="main"
        maxWidth={location.pathname.includes('/timesheets') || location.pathname.includes('/compliance') || isHidden ? false : undefined}
        style={{
          flexGrow: 1,
          minWidth: 0,
          // Announcements' dropdowns open below the last fields, and an
          // absolutely positioned panel clipped here never adds to the page
          // height, so on a short screen the last States were unreachable.
          // Clip only sideways there and let the page scroll down to them.
          // Other routes keep 'hidden'; dropping it would change how their
          // position: sticky elements behave.
          ...(location.pathname === '/admin-send-message'
            ? { overflowX: 'clip', overflowY: 'visible' }
            : { overflow: 'hidden' }),
          paddingLeft: location.pathname.includes('/timesheets')
            ? '20px'
            : location.pathname.includes('/compliance')
              ? '0'
              : isMobileApp
                ? '12px'
                : !isLoggedIn || isHidden
                  ? '0'
                  : isMobile ? '16px' : isTablet ? '24px' : '36px',
          paddingRight: location.pathname.includes('/timesheets')
            ? '20px'
            : location.pathname.includes('/compliance')
              ? '0'
              : isMobileApp
                ? '12px'
                : !isLoggedIn || isHidden
                  ? '0'
                  : isMobile ? '16px' : isTablet ? '24px' : '36px',
          marginLeft:
            location.pathname.includes('/timesheets') || !isLoggedIn || isHidden
              ? '0'
              : 'auto',
          marginRight:
            !isLoggedIn || isHidden
              ? '0'
              : location.pathname.includes('/timesheets')
                ? '0'
                : 'auto',
        }}
      >
        {!isHidden && !isMobile && (
          <Box
            sx={{
              height: '48px',
            }}
          />
        )}
        <DrawerContext.Provider value={!isHidden && isMobile && isLoggedIn ? () => setOpen(true) : null}>
          {isLoggedIn && !currentUser ? (
            <Loading />
          ) : (
            children
          )}
        </DrawerContext.Provider>
      </Container>
    </div>
  );
};

const LogoHeader = styled.div`
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #F3F4F6;
  flex-shrink: 0;
`;

export const CustomAvatar = styled(Avatar)`
  color: ${(props) => props.theme.palette.primary.onPrimary};
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
`;

const NavItemsContainer = styled.div`
  padding: 8px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionHeader = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #374151;
  padding: 16px 12px 6px 12px;
  text-transform: uppercase;
`;

/** The count pill on an open-drawer entry (pending organisations). */
const NavCount = styled('span')`
  margin-left: auto;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  min-width: 18px;
  text-align: center;
  border-radius: 9px;
  color: #fff;
  background: ${theme.palette.error.main};
`;

const NavItem = styled(ListItemButton)`
  &&& {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 12px;
    border-radius: 8px;
    border: none;
    position: relative;
    width: 100%;
    text-align: left;
    min-height: unset;
  }
`;

const ActiveIndicator = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  border-radius: 0 3px 3px 0;
  background: ${() => theme.palette.primary.main};
`;

const CollapseToggle = styled.button`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 50%;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1300;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  color: #6B7280;
  transition: left ${transitionDuration} ${transitionEasing}, all 0.15s ease;

  /* Expand touch target to 44px without changing visual size */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 44px;
    height: 44px;
  }

  &:hover {
    background: #F9FAFB;
    border-color: #D1D5DB;
  }
`;

const CustomDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  variants: [
    {
      props: ({ open }) => open,
      '& .MuiPaper-root': {
        ...openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));

const ProfileSection = styled.div`
  margin-top: auto;
  padding: 12px 16px;
  border-top: 1px solid #E5E7EB;
`;

export default Layout;

function stringToColor(string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

function stringAvatar(name) {
  return {
    sx: {
      bgcolor: stringToColor(name),
    },
    children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
  };
}
